import { format } from 'date-fns';
import { Link2 } from 'lucide-react';
import type { SurveyGroup } from 'src/types/survey-group.types';
import { Skeleton } from 'src/components/ui/skeleton';

interface SurveyGroupCardProps {
  group: SurveyGroup | undefined;
  index: number;
  /** When set, the card opens the survey group results page for this group. */
  onOpen?: () => void;
}

const SurveyGroupCard = ({ group, index, onOpen }: SurveyGroupCardProps) => {
  if (!group) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <Skeleton className="h-5 w-3/4 mb-2" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    );
  }

  const memberCount = group.memberExtSurveyIds.length;
  const qc = typeof group.questionLinkCount === 'number' ? group.questionLinkCount : undefined;
  const starts = group.started ? group.started.human + group.started.ai : undefined;
  const completes = group.completed ? group.completed.human + group.completed.ai : undefined;
  const statsLine = [qc !== undefined ? `${qc} question${qc === 1 ? '' : 's'}` : null, starts !== undefined ? `${starts} starts` : null, completes !== undefined ? `${completes} completes` : null]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (!onOpen) {
          return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className={`rounded-xl border border-violet-200/80 bg-gradient-to-br from-violet-50/90 to-white p-4 shadow-sm ${
        onOpen ? 'cursor-pointer transition-shadow hover:shadow-md hover:ring-1 hover:ring-violet-200/80' : ''
      }`}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-800">
          <Link2 className="h-3 w-3" />
          Group
        </span>
      </div>
      <div className="font-medium text-gray-900 line-clamp-2">
        {group.name}
      </div>
      <div className="mt-2 text-sm text-gray-500">
        {format(new Date(group.createdAt), 'MMM d, yyyy')}
        {' · '}
        {memberCount}
        {' '}
        member survey
        {memberCount === 1 ? '' : 's'}
      </div>
      {statsLine ? (
        <div className="mt-1 text-xs text-gray-600">
          {statsLine}
        </div>
      ) : null}
    </div>
  );
};

export default SurveyGroupCard;
